import { Text, FileButton, Avatar, Button, Loader } from "@mantine/core";
import { useEffect, useState } from "react";
import { IconPlus } from "@tabler/icons-react";
import "./BusinessLogo.scss";
import { getPreSignedUrl } from "../../../utils/api";
import type { JSX } from "react/jsx-runtime";
import { generateTaxYear } from "../../../utilities";
import axios from "axios";
import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";
import { Endpoints } from "@/utils/api/endpoints";
import { fileNameFormatter } from "@/utils/helper";
import { convertToBase64 } from "@components/Tax Pro Only/Modals/EmailModal/utils";

const currentTaxYear = generateTaxYear();

const BusinessLogo = () => {
  const storedBusinessInfo = localStorage.getItem("businessInfo");
  const initialBusinessInfo = storedBusinessInfo
    ? JSON.parse(storedBusinessInfo)
    : {
        businessName: "",
        businessPhone: "",
        businessEmail: "",
        businessWebsite: "",
        logoKey: "",
        consultLink: "",
        paymentLink: "",
      };

  const [businessInfo, setBusinessInfo] = useState(initialBusinessInfo);

  const [businessLogo, setBusinessLogo] = useState<{
    file: File | null;
    url: string;
    loading: boolean;
  }>({
    file: null,
    url: sessionStorage.getItem("logoUrl") ?? "",
    loading: false,
  });

  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Upload logo using dedicated business logo endpoint
   */
  const uploadLogo = async (file: File) => {
    setIsUploading(true);
    setUploadError("");
    setUploadSuccess(false);

    try {
      // Convert file to base64 using shared utility
      const base64Data = await convertToBase64(file);

      // Upload to dedicated logo endpoint
      const response = await axios.post(
        `${backend_url}/${Endpoints.businessLogo}`,
        {
          fileName: fileNameFormatter(file.name),
          data: base64Data,
          taxYear: currentTaxYear,
        },
        AXIOS_CONFIG
      );

      const { logoKey, logoUrl } = response.data.content;

      // Update local state with new logo URL
      setBusinessLogo({ file: null, url: logoUrl, loading: false });

      // Update local storage with new logo key
      const updatedBusinessInfo = {
        ...businessInfo,
        logoKey,
      };

      localStorage.setItem("businessInfo", JSON.stringify(updatedBusinessInfo));
      setBusinessInfo(updatedBusinessInfo);
      setUploadSuccess(true);
      setIsUploading(false);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "An unknown error occurred";
      setUploadError(errorMessage);
      setIsUploading(false);
      console.error("Failed to upload logo:", error);
    }
  };

  const handleFileChange = async (file: File | null) => {
    if (!file) return;

    // Show preview immediately
    setBusinessLogo((prev) => ({
      ...prev,
      file,
      url: URL.createObjectURL(file),
      loading: true,
    }));

    // Upload using dedicated logo endpoint
    await uploadLogo(file);
  };

  const getLogoUrl = async ({ logoKey }: { logoKey: string }) => {
    try {
      if (logoKey) {
        const parts = logoKey.split("/");
        if (parts.length >= 4) {
          const [userId, taxYear, form, fileName] = parts;

          setBusinessLogo((prev) => ({ ...prev, loading: true }));
          await new Promise((resolve) => setTimeout(resolve, 5000));
          setBusinessLogo((prev) => ({ ...prev, loading: false }));

          const response = await getPreSignedUrl(
            userId,
            fileName,
            form,
            parseInt(taxYear, 10)
          );
          sessionStorage.setItem("logoUrl", response || "");
          return (await response?.data?.url) || response;
        } else {
          console.error("Invalid logoKey format:", logoKey);
        }
      } else {
        console.error("logoKey is not defined in businessInfo");
      }
    } catch (error) {
      console.error("Failed to fetch logo URL:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      if (!businessLogo.url) {
        const link = await getLogoUrl({
          logoKey: businessInfo.logoKey,
        });
        sessionStorage.setItem("logoUrl", link || "");
        setBusinessLogo((prev) => ({
          ...prev,
          url: link || "",
          loading: false,
        }));
      }
    };
    fetchBusinessInfo();
  }, [businessLogo.url, businessInfo]);

  useEffect(() => {
    if (uploadSuccess || uploadError) {
      const timer = setTimeout(() => {
        setUploadSuccess(false);
        setUploadError("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [uploadSuccess, uploadError]);

  // Extracted logic into a variable
  let avatarContent: JSX.Element;

  if (isUploading) {
    avatarContent = <Loader color="blue" className="loading" />;
  } else if (businessLogo?.url) {
    avatarContent = (
      <Avatar
        size={82}
        className="avatar-img"
        alt="Profile"
        src={businessLogo?.url}
      />
    );
  } else {
    avatarContent = (
      <div className="logo-preview">
        {businessLogo.loading ? (
          <Loader color="blue" className="loading" />
        ) : (
          <IconPlus className="icon" />
        )}
      </div>
    );
  }
  return (
    <div className="logo-container">
      <Text className="section-title" fw={600} style={{ display: "none" }}>
        Business Logo
      </Text>
      <div className="edit-container">
        <FileButton onChange={handleFileChange} accept="image/png, image/jpeg">
          {(props) => (
            <>
              {avatarContent}
              <div className="fields">
                <Text className="edit-text" m={10} maw={150}>
                  Please upload a JPG, PNG, or GIF file and 5MB or less.
                </Text>

                <Button size="sm" color="#0077cc" radius={"md"} {...props}>
                  Upload Logo
                </Button>
              </div>
            </>
          )}
        </FileButton>
      </div>
      {isUploading && (
        <Text className="loading-message">
          Uploading... Please stay on the page.
        </Text>
      )}
      {uploadError && <Text className="error-message">{uploadError}</Text>}
      {uploadSuccess && (
        <Text className="success-message">Upload successful!</Text>
      )}
    </div>
  );
};

export default BusinessLogo;
